import ProtoBufService from './ProtoBufService'
import * as CameraInfoPB from './libs/messaging/camera_info_pb'
import { CameraRuntimeStatus as CameraRuntimeStatusPB } from './libs/messaging/camera_status_pb'
import { assignPayloadByDict } from  './utils'

export namespace CameraInfo {
  export enum ConfigType {
    ALL = 0,
    FEATURES = 1,
    TRUST_CIRCLE = 2,
    DETECTION = 3,
    CONFIG_RELOAD = 5,
  }

  export enum Facing {
    DEFAULT = 'DEFAULT',    // 0
    BACK = 'BACK',          // 1
    FRONT = 'FRONT',        // 2
    EXTERNAL = 'EXTERNAL',  // 3
  }

  export type CameraStatus = CameraRuntimeStatusPB.AsObject | undefined

  export class CameraSensorCapability {
    public facing: Facing
    public torch?: CameraInfoPB.CameraDeviceCapabilityResponse.CameraSensorCapability.TorchCapability.AsObject

    constructor (object: CameraInfoPB.CameraDeviceCapabilityResponse.CameraSensorCapability.AsObject) {
      switch (object.facing) {
        case 0:
          this.facing = Facing.DEFAULT
          break
        case 1:
          this.facing = Facing.BACK
          break
        case 2:
          this.facing = Facing.FRONT
          break
        case 3:
          this.facing = Facing.EXTERNAL
          break
        default:
          this.facing = Facing.DEFAULT
          break
      }
      this.torch = object.torch      
    }
  }

  export enum RotationCapability {
    ROTATION_NOT_SUPPORTED = 'ROTATION_NOT_SUPPORTED',  // 0
    ROTATE_BY_90_DEGREES = 'ROTATE_BY_90_DEGREES',      // 1
    ROTATE_BY_180_DEGREES = 'ROTATE_BY_180_DEGREES',    // 2
  }

  export enum ResolutionChangeCapability {
    RESOLUTION_CHANGE_NOT_SUPPORTED = 'RESOLUTION_CHANGE_NOT_SUPPORTED',  // 0
    APP_TYPE_1 = 'APP_TYPE_1',                                            // 1
    HARDWARE_TYPE_1 = 'HARDWARE_TYPE_1',                                  // 8
  }

  export enum ZoomCapability {
    ZOOM_NOT_SUPPORTED = 'ZOOM_NOT_SUPPORTED',  // 0
    ZOOM_1 = 'ZOOM_1',                          // 1
    ZOOM_2 = 'ZOOM_2',                          // 2
  }

  export class CameraDeviceCapability {
    public protocol: number
    public datachannel: boolean
    public cameraPipeline: boolean
    public cameraSensorCapabilityList: CameraSensorCapability[]
    public hardware: boolean
    public batteryless: boolean
    public statusLed: boolean
    public irNightVision: boolean
    public disableCamera: boolean
    public lowlightFilter: boolean
    public orientationControl: RotationCapability
    public rotationSetting: RotationCapability
    public autoPowerSaving: boolean
    public recordingAudio: boolean
    public osd: boolean
    public maxConnectedViewer: number
    public accessPriority: boolean
    public twoWayAudio: boolean
    public manualRecording: boolean
    public siren: boolean
    public resolutionChange: ResolutionChangeCapability
    public zoom: ZoomCapability
    public detectionModeMotion: boolean
    public detectionModePerson: boolean
    public detectionReminder: boolean
    public detectionSchedule: boolean
    public detectionZone: boolean
    public liveAndDetection: boolean
    public continuousRecording: boolean
    public cameraHealth: boolean
    public cameraLog: boolean
    public appLock: boolean
    public rebootCamera: boolean
    public networkStatus: boolean
    public firmwareUpdate: boolean
    public sdcardManagement: boolean
    public wifiManagement: boolean

    constructor (object: CameraInfoPB.CameraDeviceCapabilityResponse.AsObject) {
      this.protocol = object.protocol
      this.datachannel = object.datachannel > 0
      this.cameraPipeline = object.cameraPipeline > 0
      this.cameraSensorCapabilityList = object.cameraSensorCapabilityList.map(data => new CameraSensorCapability(data))
      this.hardware = object.hardware > 0
      this.batteryless = object.batteryless > 0
      this.statusLed = object.statusLed > 0
      this.irNightVision = object.irNightVision > 0
      this.disableCamera = object.disableCamera > 0
      this.lowlightFilter = object.lowlightFilter > 0
      switch (object.orientationControl) {
        case 0:
          this.orientationControl = RotationCapability.ROTATION_NOT_SUPPORTED
          break
        case 1:
          this.orientationControl = RotationCapability.ROTATE_BY_90_DEGREES
          break
        case 2:
          this.orientationControl = RotationCapability.ROTATE_BY_180_DEGREES
          break
        default:
          this.orientationControl = RotationCapability.ROTATION_NOT_SUPPORTED
          break
      }
      switch (object.rotationSetting) {
        case 0:
          this.rotationSetting = RotationCapability.ROTATION_NOT_SUPPORTED
          break
        case 1:
          this.rotationSetting = RotationCapability.ROTATE_BY_90_DEGREES
          break
        case 2:
          this.rotationSetting = RotationCapability.ROTATE_BY_180_DEGREES
          break
        default:
          this.rotationSetting = RotationCapability.ROTATION_NOT_SUPPORTED
          break
      }
      this.autoPowerSaving = object.autoPowerSaving  > 0
      this.recordingAudio = object.recordingAudio > 0
      this.osd = object.osd > 0
      this.maxConnectedViewer = object.maxConnectedViewer || 0
      this.accessPriority = object.accessPriority > 0
      this.twoWayAudio = object.twoWayAudio > 0
      this.manualRecording = object.manualRecording > 0
      this.siren = object.siren > 0
      switch (object.resolutionChange) {
        case 0:
          this.resolutionChange = ResolutionChangeCapability.RESOLUTION_CHANGE_NOT_SUPPORTED
          break
        case 1:
          this.resolutionChange = ResolutionChangeCapability.APP_TYPE_1
          break
        case 8:
          this.resolutionChange = ResolutionChangeCapability.HARDWARE_TYPE_1
          break
        default:
          this.resolutionChange = ResolutionChangeCapability.RESOLUTION_CHANGE_NOT_SUPPORTED
          break
      }
      switch (object.zoom) {
        case 0:
          this.zoom = ZoomCapability.ZOOM_NOT_SUPPORTED
          break
        case 1:
          this.zoom = ZoomCapability.ZOOM_1
          break
        case 2:
          this.zoom = ZoomCapability.ZOOM_2
          break
        default:
          this.zoom = ZoomCapability.ZOOM_NOT_SUPPORTED
          break
      }
      this.detectionModeMotion = object.detectionModeMotion > 0
      this.detectionModePerson = object.detectionModePerson > 0
      this.detectionReminder = object.detectionReminder > 0
      this.detectionSchedule = object.detectionSchedule > 0
      this.detectionZone = object.detectionZone > 0
      this.liveAndDetection = object.liveAndDetection > 0
      this.continuousRecording = object.continuousRecording > 0
      this.cameraHealth = object.cameraHealth > 0
      this.cameraLog = object.cameraLog > 0
      this.appLock = object.appLock > 0
      this.rebootCamera = object.rebootCamera > 0
      this.networkStatus = object.networkStatus > 0
      this.firmwareUpdate = object.firmwareUpdate > 0
      this.sdcardManagement = object.sdcardManagement > 0
      this.wifiManagement = object.wifiManagement > 0
    }
  }

  export class CameraInfoService extends ProtoBufService {
    webcamCapability: { [K: string]: any; }
    
    constructor () {
      super()
      this.service = ProtoBufService.ServiceType.CAMERA_INFO
      
      // webcamCapability will be the same for every webcam
      this.webcamCapability = {
        camera_sensor_capability_list: [new CameraInfoPB.CameraDeviceCapabilityResponse.CameraSensorCapability()],
        orientation_control: CameraInfoPB.CameraDeviceCapabilityResponse.RotationCapability.ROTATION_NOT_SUPPORTED,
        rotation_setting: CameraInfoPB.CameraDeviceCapabilityResponse.RotationCapability.ROTATION_NOT_SUPPORTED,
        resolution_change: CameraInfoPB.CameraDeviceCapabilityResponse.ResolutionChangeCapability.APP_TYPE_1,
        zoom: CameraInfoPB.CameraDeviceCapabilityResponse.ZoomCapability.ZOOM_2,
        max_connected_viewer: 1,
        batteryless: 1,
        
        protocol: 1, // protobuf over xmpp protocal version(in integer)
        recording_audio: 0,
        disableCamera: 1,
        lowlightFilter: 1,
        detectionModeMotion: 1,
        detectionModePerson: 1,
        liveAndDetection: 1,
      }
      this.webcamCapability.camera_sensor_capability_list[0].setTorch(new CameraInfoPB.CameraDeviceCapabilityResponse.CameraSensorCapability.TorchCapability())
      this.webcamCapability.camera_sensor_capability_list[0].getTorch().setSupported(false)
    }

    updateViewerCapability(id: number, version: number) {
      const payload = new CameraInfoPB.UpdateViewerCapability()
      payload.setProtocolVersion(version)
      return this.request(id, CameraInfoPB.CameraInfoControls.VIEWER_CAPS, payload)
    }

    requestCameraDeviceCapability(id: number) {
      const payload = new CameraInfoPB.CameraDeviceCapabilityRequest()
      return this.request(id, CameraInfoPB.CameraInfoControls.CAMERA_CAPS, payload)
    }
  
    responseCameraDeviceCapability(id: number) {
      const webcamCapability = this.webcamCapability
      const payload = new CameraInfoPB.CameraDeviceCapabilityResponse()

      assignPayloadByDict(payload, webcamCapability)
      return this.response(id, CameraInfoPB.CameraInfoControls.CAMERA_CAPS, payload)
    }

    requestCameraSnapshot(id: number, width: number, height: number) {
      const payload = new CameraInfoPB.CameraSnapshotRequest()
      payload.setWidth(width)
      payload.setHeight(height)
      return this.request(id, CameraInfoPB.CameraInfoControls.SNAPSHOT, payload)
    }

    responseCameraSnapshot(id: number, snapshot: string | Uint8Array) {
      const payload = new CameraInfoPB.CameraSnapshotResponse()
      payload.setSnapshot(snapshot)
      return this.response(id, CameraInfoPB.CameraInfoControls.SNAPSHOT, payload)
    }

    requestCameraStatus(id: number) {
      const payload = new CameraInfoPB.CameraStatusRequest()
      return this.request(id, CameraInfoPB.CameraInfoControls.STATUS_REQUEST, payload)
    }

    requestConfigReload(id: number, type: ConfigType) {
      const payload = new CameraInfoPB.ConfigReloadRequest()
      switch (type) {
        case ConfigType.ALL:
          payload.setType(CameraInfoPB.ConfigReloadRequest.ConfigType.ALL)
          break
        case ConfigType.FEATURES:
          payload.setType(CameraInfoPB.ConfigReloadRequest.ConfigType.FEATURES)
          break
        case ConfigType.TRUST_CIRCLE:
          payload.setType(CameraInfoPB.ConfigReloadRequest.ConfigType.TRUST_CIRCLE)
          break
        case ConfigType.DETECTION:
          payload.setType(CameraInfoPB.ConfigReloadRequest.ConfigType.DETECTION)
          break
      }
      return this.request(id, CameraInfoPB.CameraInfoControls.CONFIG_RELOAD, payload)
    }

    requestCameraHealthStatus(id: number) {
      const payload = new CameraInfoPB.CameraHealthStatusRequest()
      return this.request(id, CameraInfoPB.CameraInfoControls.CAMERA_HEALTH, payload)
    }

    responseCameraHealthStatus(id: number, supportedTips: number[], statusCodes: number[]) {
      const payload = new CameraInfoPB.CameraHealthStatusResponse()
      payload.setSupportedTipsList(supportedTips)
      payload.setStatusCodesList(statusCodes)
      return this.response(id, CameraInfoPB.CameraInfoControls.CAMERA_HEALTH, payload)
    }
    
    requestCameraLog(id: number) {
      const payload = new CameraInfoPB.CameraLogRequest()
      return this.request(id, CameraInfoPB.CameraInfoControls.CAMERA_LOG, payload)
    }

    responseCameraLog(id: number, jsonStr: string) {
      const payload = new CameraInfoPB.CameraLogResponse()
      payload.setLog(jsonStr)
      return this.response(id, CameraInfoPB.CameraInfoControls.CAMERA_LOG, payload)
    }

    responseCameraStatus(id: number, status: CameraRuntimeStatusPB.AsObject) {
      const payload = new CameraInfoPB.CameraStatusResponse()
      const statusPB = new CameraRuntimeStatusPB()

      let objs = {
        powerStatus: new CameraRuntimeStatusPB.PowerSupplyStatus(),
        detectorStatus: new CameraRuntimeStatusPB.DetectorStatus(),
        healthStatus: new CameraRuntimeStatusPB.HealthStatus(),
      }
      let primals = {
        settingsRevision: status.settingsRevision,
        disabled: status.disabled,
        micOff: status.micOff,
        connectedUsersList: status.connectedUsersList,
        statusCodesList: status.statusCodesList,
        membershipStatus: status.membershipStatus,
      }
      for (const key in objs) {
        // @ts-ignore: TS7053
        assignPayloadByDict(objs[key], status[key])
      }

      assignPayloadByDict(statusPB, primals)
      assignPayloadByDict(statusPB, objs)
      payload.setStatus(statusPB);
      return this.response(id, CameraInfoPB.CameraInfoControls.STATUS_REQUEST, payload)
    }

    onRequest({ control, payload }: { control: number, payload: Uint8Array }) {
      let data;
      switch (control) {
        case CameraInfoPB.CameraInfoControls.VIEWER_CAPS:
          data = CameraInfoPB.UpdateViewerCapability.deserializeBinary(payload)
          return {
            type: 'updateViewerCapability',
            data: data.toObject()
          }
        case CameraInfoPB.CameraInfoControls.CAMERA_CAPS:
          return {
            type: 'getCameraDeviceCapability',
            data: null,
          }
        case CameraInfoPB.CameraInfoControls.SNAPSHOT:
          data = CameraInfoPB.CameraSnapshotRequest.deserializeBinary(payload)
          return {
            type: 'getSnapshot',
            data: data.toObject()
          }
        case CameraInfoPB.CameraInfoControls.STATUS_REQUEST:
          return {
            type: 'getCameraStatus',
            data: null,
          }
        case CameraInfoPB.CameraInfoControls.CONFIG_RELOAD:
          data = CameraInfoPB.ConfigReloadRequest.deserializeBinary(payload)
          return {
            type: 'reloadConfig',
            data: data.toObject()
          }
        case CameraInfoPB.CameraInfoControls.CAMERA_HEALTH:
          return {
            type: 'getCameraHealthStatus',
            data: null,
          }
        case CameraInfoPB.CameraInfoControls.CAMERA_LOG:
          return {
            type: 'getCameraLog',
            data: null,
          }
        default:
          break
      }
      return null;
    }

    onResponse({ control, payload }: { control: number, payload: Uint8Array }) {
      let data;
      switch (control) {
        case CameraInfoPB.CameraInfoControls.VIEWER_CAPS:
          return null
        case CameraInfoPB.CameraInfoControls.CAMERA_CAPS:
          data = CameraInfoPB.CameraDeviceCapabilityResponse.deserializeBinary(payload)
          return {
            type: 'cameraDeviceCapability',
            data: new CameraDeviceCapability(data.toObject())
          }
        case CameraInfoPB.CameraInfoControls.SNAPSHOT:
          data = CameraInfoPB.CameraSnapshotResponse.deserializeBinary(payload)
          return {
            type: 'snapshot',
            data: data.toObject().snapshot
          }
        case CameraInfoPB.CameraInfoControls.STATUS_REQUEST:
          data = CameraInfoPB.CameraStatusResponse.deserializeBinary(payload)
          return {
            type: 'cameraStatus',
            data: data.toObject().status as CameraStatus
          }
        case CameraInfoPB.CameraInfoControls.CONFIG_RELOAD:
          data = ProtoBufService.prototype.onResponse.call(this, { control, payload })
          return {
            type: 'configReload',
            data
          }
        case CameraInfoPB.CameraInfoControls.CAMERA_HEALTH:
          data = CameraInfoPB.CameraHealthStatusResponse.deserializeBinary(payload)
          return {
            type: 'cameraHealth',
            data: data.toObject()
          }
        case CameraInfoPB.CameraInfoControls.CAMERA_LOG:
          data = CameraInfoPB.CameraLogResponse.deserializeBinary(payload)
          return {
            type: 'cameraLog',
            data: data.toObject()
          }
        default:
          break
      }
      return null
    }
  }
}

export default CameraInfo.CameraInfoService
